import { useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { InputComponent } from '../../../../../shared/InputComponent';
import styles from '../edit-budget-form.module.css';
import { BudgetFormDTO } from '../BudgetFormDTO';
import {
  maskCpfCnpj,
  maskKilometer,
  maskLicensePlate,
  maskPhone,
} from '../../../../../services/helpers/mask';
import { fetchVehicleDataByLicensePlateController } from '../../../../../services/dataFetchers/VehicleDataFetcher/VehicleDataFetcher';
import { fetchCustomerDataByDocumentController } from '../../../../../services/dataFetchers/customerDataFetcher/customerDataFetcher';
import { KmModal } from '../../../OrderService/components/KmModal';

export const CustomerForm = () => {
  const {
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<BudgetFormDTO>();
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);
  const [isKmModalOpen, setIsKmModalOpen] = useState(false);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);

  // verifica se os campos já estão preenchidos para não fazer outra requisição
  const isVehicleDataFilled = useCallback(() => {
    const values = getValues();
    return (
      values.vehicle.vehicle.trim() !== '' &&
      values.vehicle.chassi.trim() !== ''
    );
  }, [getValues]);

  const plate = watch('vehicle.plate');
  const document = watch('customer.document');

  const fetchVehicleInfo = useCallback(async () => {
    if (plate.length === 7) {
      if (!isVehicleDataFilled()) {
        try {
          setIsLoadingVehicle(true);
          const res = await fetchVehicleDataByLicensePlateController(plate);

          setValue('vehicle.vehicle', res?.name || 'Não encontrado');
          setValue('vehicle.chassi', `${res?.chassi || ''}`);
          setValue('vehicle.km', `${res?.current_km || ''}`);
          setValue('vehicle.lastKm', `${res?.current_km || ''}`);

          // garante que a busca por placa não sobrescreva os dados que já foram preenchidos
          const { customer: { name: currentName, phone: currentPhone, document: currentDocument } } = getValues();
          setValue('customer.name', currentName.trim().length > 0 ? currentName : res?.user?.name || '');
          setValue('customer.document', currentDocument.trim().length > 0 ? currentDocument : maskCpfCnpj(res?.user?.document || ''));
          setValue('customer.phone', currentPhone.trim().length > 0 ? currentPhone : maskPhone(res?.user?.phone || ''));

          setIsKmModalOpen(true);

          // handlePlate(plate);
        } catch (error) {
          console.error('Erro ao buscar veículo:', error);
        } finally {
          setIsLoadingVehicle(false);
        }
      }
    } else {
      setValue('vehicle.vehicle', '');
      setValue('vehicle.chassi', '');
      setValue('vehicle.km', '');
      setValue('vehicle.lastKm', '');

      setValue('customer.name', '');
      setValue('customer.document', '');
      setValue('customer.phone', '');
    }
  }, [isVehicleDataFilled, plate, setValue]);

  const handleDocument = useCallback(async () => {

    if (document.length === 14 || document.length === 18) {
      setIsLoadingCustomer(true);
      try {
        const res = await fetchCustomerDataByDocumentController(document);

        if (res) {
          // garante que a busca por cliente não sobrescreva os dados que já foram preenchidos
          const { customer: { name: currentName, phone: currentPhone } } = getValues();
          setValue('customer.name', currentName.trim().length > 0 ? currentName : res?.name ?? '');
          setValue('customer.phone', currentPhone.trim().length > 0 ? currentPhone : maskPhone(res?.phone ?? ''));
        }
      } catch (err) {
        console.error(err);
        setIsLoadingCustomer(false);
      } finally {
        setIsLoadingCustomer(false);
      }
    } else {
      setValue('customer.name', '');
      setValue('customer.phone', '');
    }
  }, [document, setValue]);

  useEffect(() => {
    fetchVehicleInfo();
  }, [fetchVehicleInfo]);

  useEffect(() => {
    handleDocument();
  }, [handleDocument]);

  return (
    <form className={`${styles.form}`}>
      {isKmModalOpen && <KmModal handleClose={() => setIsKmModalOpen(false)} />}
      <section>
        <div className={styles['section-header']}>
          <h2>Veículo</h2>
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            {...register('vehicle.plate')}
            label="Placa:"
            className={styles.input}
            maxLength={7}
            onChange={e => {
              setValue('vehicle.plate', maskLicensePlate(e.target.value));
            }}
            isLoading={isLoadingVehicle}
            hint={errors.vehicle?.plate?.message}
            state={errors.vehicle?.plate?.message ? 'error' : 'default'}
          />
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            {...register('vehicle.vehicle')}
            label="Veículo:"
            className={styles.input}
            disabled
          />
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            {...register('vehicle.chassi')}
            label="Chassi:"
            className={styles.input}
            disabled
          />
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            {...register('vehicle.km')}
            label="KM:"
            className={styles.input}
            onChange={e => {
              setValue('vehicle.km', maskKilometer(e.target.value));
            }}
            hint={errors.vehicle?.km?.message}
            state={errors.vehicle?.km?.message ? 'error' : 'default'}
          />
        </div>
      </section>

      <section>
        <div className={styles['section-header']}>
          <h2>Cliente</h2>
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            {...register('customer.document')}
            label="CPF/CNPJ:"
            className={styles.input}
            onChange={e => {
              setValue('customer.document', maskCpfCnpj(e.target.value));
            }}
            isLoading={isLoadingCustomer}
          />
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            required
            {...register('customer.name')}
            label="Nome:"
            className={styles.input}
            hint={errors.customer?.name?.message}
            state={errors.customer?.name?.message ? 'error' : 'default'}
          />
        </div>
        <div className={styles['form-row']}>
          <InputComponent
            required
            {...register('customer.phone')}
            label="Telefone:"
            className={styles.input}
            onChange={e => {
              setValue('customer.phone', maskPhone(e.target.value));
            }}
            hint={errors.customer?.phone?.message}
            state={errors.customer?.phone?.message ? 'error' : 'default'}
          />
        </div>
      </section>
    </form>
  );
};
